import { Component, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { BackendService } from 'app/shared/services/backend.service';
import { ButtonComponent } from '../../shared/components/button/button.component';
import { BannerComponent } from '../../shared/components/banner/banner.component';
import { BackendErrorType } from '../../shared/services/errors/backend.errors';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { AuthenticationStateService } from 'app/shared/services/state/authentication-state.service';

@Component({
  selector: 'ez-personal-access-token',
  imports: [ReactiveFormsModule, MainLayoutComponent, ButtonComponent, BannerComponent],
  templateUrl: './personal-access-token.component.html',
  styleUrl: './personal-access-token.component.scss',
})
export class PersonalAccessTokenComponent {
  private readonly backendService = inject(BackendService);
  private readonly router = inject(Router);
  private readonly logger = inject(NGXLogger);
  private readonly landingpageConfigurationStateService = inject(LandingpageConfigurationStateService);
  private readonly authenticationStateService = inject(AuthenticationStateService);

  readonly form = new FormGroup({
    personalAccessToken: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required],
    }),
  });
  readonly submitPersonalAccessTokenIsLoading = signal(false);

  readonly errorMessage = signal<string | null>(null);

  constructor() {
    this.tokenInput.events
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.errorMessage.set(this.currentErrorMessage()));
  }

  get tokenInput(): AbstractControl {
    return this.form.controls.personalAccessToken;
  }

  private currentErrorMessage(): string | null {
    const tokenInput = this.tokenInput;
    if (!tokenInput.invalid || !(tokenInput.dirty || tokenInput.touched)) {
      return null;
    }
    if (tokenInput.errors?.['required']) {
      return 'Fehler: Dies ist ein Pflichtfeld. Bitte geben Sie Ihren Zugangscode ein.';
    }
    if (tokenInput.errors?.[BackendErrorType.CLIENT_ERROR]) {
      return 'Fehler: Bitte überprüfen Sie Ihre Eingabe.';
    }
    return 'Fehler: Ein Fehler ist aufgetreten. Bitte versuchen Sie es erneut.';
  }

  onSubmit() {
    const personalAccessToken = this.form.controls['personalAccessToken'].value;

    this.submitPersonalAccessTokenIsLoading.set(true);
    this.landingpageConfigurationStateService
      .fetchLandingpageConfigurationForToken(personalAccessToken)
      .then(() => {
        this.logger.debug('Submit personal access token:', {
          requestParam: personalAccessToken,
        });
        this.router.navigate(['case']);
      })
      .catch((error: Error) => {
        if (error.message === BackendErrorType.ALREADY_SENT) {
          this.router.navigate(['already-sent']);
        } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
          this.router.navigate(['verification-error']);
        } else if (error.message !== BackendErrorType.SERVER_ERROR) {
          // SERVER_ERROR is excluded because the errorInterceptor navigates to the error page;
          // showing an inline message here would only flash on a page the user is leaving.
          this.tokenInput.setErrors({ [error.message]: true });
        }
      })
      .finally(() => this.submitPersonalAccessTokenIsLoading.set(false));
  }
}
